import React, { useEffect, useRef, useState, useCallback } from 'react';
import AgoraRTC from "agora-rtc-sdk-ng";
import { changeVideo, changeAudio, turnOffCamera, turnOnCamera, turnOffMic, turnOnMic, setCamBeforeScreenShare } from "../../components/streamControls/controls.actions";
import useAppDispatch from '../../../../hooks/useAppDispatch';
import useAppAccessor from '../../../../hooks/useappAccessor';
import { CAMOFF, No_Permission, Camera, Microphone, MICOFF } from '../../../../assets/icons';
import { AiOutlineDown } from 'react-icons/ai';

interface CameraPreviewProps {
  onGoLive?: () => void;
}

const CameraPreview = ({ onGoLive }: CameraPreviewProps) => {
  const dispatch = useAppDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const currentCameraIdRef = useRef<string | null>(null);
  
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [selectedMic, setSelectedMic] = useState<string>('');
  const [cameraDevices, setCameraDevices] = useState<MediaDeviceInfo[]>([]);
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [showCameraDropdown, setShowCameraDropdown] = useState(false);
  const [showMicDropdown, setShowMicDropdown] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { getControls } = useAppAccessor();
  const { isCameraOn, isMicOn, newVideoStream, newAudioStream } = getControls();
  const { getUserData } = useAppAccessor();
  const { language } = getUserData();

  const stopPreview = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      audioStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    currentCameraIdRef.current = null;
  }, []);


  const startPreview = useCallback(async (deviceId: string, currentVideoStream: string | null) => {
    try {
      if (currentVideoStream && deviceId !== currentVideoStream) {
        stopPreview();
      } else if (!currentVideoStream) {
        stopPreview();
      }

      const availableDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = availableDevices.filter(d => d.kind === 'videoinput');
      const deviceExists = videoDevices.find(d => d.deviceId === deviceId);

      if (!deviceExists && deviceId) {
        throw new Error(`Camera device ${deviceId} not found`);
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        streamRef.current = null;
      }
      
      await new Promise(resolve => setTimeout(resolve, 50));

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: deviceId ? { 
            deviceId: { exact: deviceId } 
          } : true
        });
      } catch (exactError: any) {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { deviceId: deviceId || undefined } 
        });
      }
      
      const actualDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId;
      const trackLabel = stream.getVideoTracks()[0]?.label;

      if (deviceId && actualDeviceId !== deviceId) {
        
        stream.getTracks().forEach(track => track.stop());
        stream = null;
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        try {
          stream = await navigator.mediaDevices.getUserMedia({ 
            video: { deviceId: { exact: deviceId } } 
          });
          const retryDeviceId = stream.getVideoTracks()[0]?.getSettings().deviceId;
          
          if (retryDeviceId !== deviceId) {
            throw new Error(`Unable to switch to camera. Browser returned ${retryDeviceId} instead of ${deviceId}.`);
          }
        } catch (retryError: any) {
          throw new Error(`Unable to switch to camera ${deviceId}. Browser may not support this device or it's already in use.`);
        }
      }
      
      streamRef.current = stream;
      setHasPermission(true);
      setError(null);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      currentCameraIdRef.current = deviceId;
      if (deviceId && deviceId !== currentVideoStream) {
        dispatch(changeVideo(deviceId));
      } else {
      }
    } catch (err: any) {
      setHasPermission(false);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError' || err.message?.includes('not found')) {
        setError('Camera device not found. The selected camera may be disconnected or unavailable.');
      } else if (err.message?.includes('Browser returned')) {
        setError(err.message);
      } else {
        setError('Failed to access camera. Please check your camera connection.');
      }
      
      stopPreview();
    }
  }, [dispatch, stopPreview]);

  const handleCameraChange = useCallback(async (deviceId: string) => {
    // console.log('[LIVE] handleCameraChange called', { 
    //   newDeviceId: deviceId, 
    //   previousSelectedCamera: selectedCamera,
    //   currentRef: currentCameraIdRef.current,
    //   isCameraOn 
    // });
    const previousCameraId = selectedCamera;
    setSelectedCamera(deviceId);
    // console.log('[LIVE] Dispatching changeVideo action', { deviceId });
    dispatch(changeVideo(deviceId));
    if (isCameraOn) {
      // console.log('[LIVE] Camera is on, starting preview with new device');
      // Pass previous camera ID for comparison, use new deviceId for the preview
      await startPreview(deviceId, previousCameraId || null);
    } else {
      // console.log('[LIVE] Camera is off, skipping preview start');
    }
    setShowCameraDropdown(false);
  }, [dispatch, isCameraOn, selectedCamera, startPreview]);

  const handleMicChange = useCallback(async (deviceId: string) => {
    setSelectedMic(deviceId);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId } });
      audioStreamRef.current = stream;
      dispatch(changeAudio(deviceId));
    } catch (err) {
      // console.error('Error changing microphone:', err);
    }
    setShowMicDropdown(false);
  }, [dispatch]);

  useEffect(() => {
    const initializeDevices = async () => {
      try {
        const cameras = await AgoraRTC.getCameras();
        const mics = await AgoraRTC.getMicrophones();
        setCameraDevices(cameras);
        setMicDevices(mics);

        if (newVideoStream && cameras.find(c => c.deviceId === newVideoStream)) {
          setSelectedCamera(newVideoStream);
        } else if (cameras.length > 0) {
          setSelectedCamera(cameras[0].deviceId);
          dispatch(changeVideo(cameras[0].deviceId));
        }

        if (newAudioStream && mics.find(m => m.deviceId === newAudioStream)) {
          setSelectedMic(newAudioStream);
        } else if (mics.length > 0) {
          setSelectedMic(mics[0].deviceId);
          dispatch(changeAudio(mics[0].deviceId));
        }
      } catch (err) {
        // console.error('Error getting devices:', err);
      }
    };
    initializeDevices();
  }, [dispatch, newVideoStream, newAudioStream]);

  useEffect(() => {
    if (newVideoStream && newVideoStream !== selectedCamera) {
      setSelectedCamera(newVideoStream);
    }
  }, [newVideoStream, selectedCamera]);

  useEffect(() => {
    if (newAudioStream && newAudioStream !== selectedMic) {
      setSelectedMic(newAudioStream);
    }
  }, [newAudioStream, selectedMic]);

  useEffect(() => {
    // console.log('[LIVE] Camera preview useEffect triggered', { 
    //   isCameraOn, 
    //   newVideoStream, 
    //   selectedCamera,
    //   currentRef: currentCameraIdRef.current 
    // });
    if (isCameraOn) {
      const cameraId = newVideoStream || selectedCamera;
      // console.log('[LIVE] Camera is on, checking if preview needed', { cameraId });
      if (cameraId) {
        // Only start preview if camera actually changed
        const currentStream = currentCameraIdRef.current;
        // console.log('[LIVE] Comparing camera IDs', { currentStream, cameraId, needsChange: !currentStream || currentStream !== cameraId });
        if (!currentStream || currentStream !== cameraId) {
          // console.log('[LIVE] Starting preview from useEffect', { cameraId, currentStream });
          startPreview(cameraId, currentStream || null);
        } else {
          // console.log('[LIVE] Skipping preview - camera unchanged');
        }
      } else {
        // console.log('[LIVE] No camera ID available');
      }
    } else {
      // console.log('[LIVE] Camera is off, stopping preview');
      stopPreview();
      currentCameraIdRef.current = null;
    }
  }, [isCameraOn, newVideoStream, selectedCamera, startPreview, stopPreview]);

  useEffect(() => {
    if (isMicOn && selectedMic) {
      const startMic = async () => {
        try {
          if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
          }

          const stream = await navigator.mediaDevices.getUserMedia({ 
            audio: { deviceId: selectedMic } 
          });
          audioStreamRef.current = stream;
        } catch (err) {
          // console.error('Error starting microphone:', err);
        }
      };
      startMic();
    } else {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
    }

    return () => {
      if (!isMicOn && audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
    };
  }, [isMicOn, selectedMic]);


  const getSelectedCameraLabel = () => {
    const device = cameraDevices.find(d => d.deviceId === selectedCamera);
    const label = device?.label || language?.['camera_prompt'] || 'Select Camera';
    if (label.length > 20) {
      return label.substring(0, 20) + '...';
    }
    return label;
  };

  const getSelectedMicLabel = () => {
    const device = micDevices.find(d => d.deviceId === selectedMic);
    const label = device?.label || language?.['microphone_prompt'] || 'Select Microphone';
    if (label.length > 20) {
      return label.substring(0, 20) + '...';
    }
    return label;
  };

  return (
    <div className="w-full h-full flex flex-col relative" style={{ backgroundColor: '#000000' }}>
      {/* Camera Preview Video - Full Screen */}
      <div className="flex-1 flex items-center justify-center relative pb-48">
        {/* PREVIEW Indicator Badge - Top Left Corner */}
        <div
          className="absolute top-10 left-4 z-10"
          style={{ zIndex: 9999 }}
        >
          <div className="flex items-center justify-center bg-[#1a1a1a]/90 backdrop-blur-md rounded-lg px-5 py-2.5 border border-[#DA414A]/50 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-[#DA414A] rounded-full"></div>
              <span className="text-white text-sm font-medium">
                {language?.['preview'] || 'PREVIEW'}
              </span>
            </div>
          </div>
        </div>

        {hasPermission !== false && isCameraOn && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-contain"
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        )}

        {(hasPermission === false || !isCameraOn || error) && (
          <div className="flex flex-col items-center justify-center w-full h-full px-4">
            {error ? (
              <div className="flex flex-col items-center justify-center bg-[#1a1a1a]/[0.6] rounded-2xl p-8 max-w-md border border-[#333]/[0.5]">
                <div className="mb-6 p-4 bg-[#DA414A]/[0.1] rounded-full">
                  <img 
                    src={No_Permission} 
                    alt="No camera permission" 
                    className="opacity-90"
                    style={{ height: 64, width: 64 }}
                  />
                </div>
                <p className="text-white text-center text-base font-medium mb-2">
                  Camera Access Required
                </p>
                <p className="text-gray-400 text-center text-sm leading-relaxed">
                  {error}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="mb-6 p-4 bg-[#2a2a2a] rounded-full opacity-80">
                  <img 
                    src={CAMOFF} 
                    alt="Camera off" 
                    style={{ height: 72, width: 72 }}
                  />
                </div>
                <p className="text-white text-center text-lg font-semibold mb-2">
                  Camera is off
                </p>
                <p className="text-gray-400 text-center text-sm max-w-sm">
                  Turn on your camera using the controls below to see a preview
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating Bottom Controls Panel - Teams/Zoom Style */}
      <div className="absolute bottom-0 left-0 right-0 z-20 pb-3 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-[#1a1a1a]/95 backdrop-blur-lg rounded-2xl border border-[#333]/50 shadow-2xl p-6">
            {/* Horizontal Layout: Toggle + Dropdown + Go Live */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3 mb-6 justify-center">
            {/* Camera Section */}
            <div className="flex items-center sm:items-end gap-2">
              {/* Camera Toggle Button */}
              <button
                onClick={() => {
                  if (isCameraOn) {
                    dispatch(turnOffCamera());
                    dispatch(setCamBeforeScreenShare(false));
                  } else {
                    dispatch(turnOnCamera());
                    dispatch(setCamBeforeScreenShare(true));
                  }
                }}
                className={`flex items-center justify-center p-2.5 rounded-xl border transition-all duration-200 shadow-lg hover:shadow-xl shrink-0 ${
                  isCameraOn 
                    ? 'bg-[#2a2a2a] border-[#3a3a3a]' 
                    : 'bg-[#DA414A]/20 border-[#DA414A]/40'
                } hover:bg-[#2a2a2a] text-white`}
                title={isCameraOn ? (language?.['turn_off_camera'] || 'Turn off camera') : (language?.['turn_on_camera'] || 'Turn on camera')}
              >
                <img
                  src={isCameraOn ? Camera : CAMOFF}
                  alt="camera"
                  height={20}
                  width={20}
                  style={{ height: '20px', width: '20px' }}
                />
              </button>
              
              {/* Camera Dropdown */}
              <div className="relative">
                <label className="block text-white text-xs font-medium mb-1.5">
                  {language?.['select_camera'] || 'Camera'}
                </label>
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowCameraDropdown(!showCameraDropdown);
                      setShowMicDropdown(false);
                    }}
                    className="w-full bg-[#202121] hover:bg-[#2a2a2a] text-white px-3 py-2.5 rounded-lg flex items-center justify-between border border-[#333] transition-colors min-w-[180px]"
                  >
                    <span className="text-xs truncate pr-2">{getSelectedCameraLabel()}</span>
                    <AiOutlineDown className="flex-shrink-0" size={14} />
                  </button>
                  {showCameraDropdown && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#202121] border border-[#333] rounded-lg shadow-xl max-h-60 overflow-y-auto z-30" style={{ maxHeight: '240px' }}>
                      {cameraDevices.map((device) => (
                        <button
                          key={device.deviceId}
                          onClick={() => handleCameraChange(device.deviceId)}
                          className={`w-full text-left px-3 py-2.5 text-xs text-white hover:bg-[#2a2a2a] transition-colors ${
                            selectedCamera === device.deviceId ? 'bg-[#DA414A]/20' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {selectedCamera === device.deviceId && (
                              <span className="text-[#DA414A]">●</span>
                            )}
                            <span className={selectedCamera === device.deviceId ? 'font-medium' : ''}>
                              {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Microphone Section */}
            <div className="flex items-center sm:items-end gap-2">
              {/* Microphone Toggle Button */}
              <button
                onClick={() => {
                  isMicOn ? dispatch(turnOffMic()) : dispatch(turnOnMic());
                }}
                className={`flex items-center justify-center p-2.5 rounded-xl border transition-all duration-200 shadow-lg hover:shadow-xl shrink-0 ${
                  isMicOn 
                    ? 'bg-[#2a2a2a] border-[#3a3a3a]' 
                    : 'bg-[#DA414A]/20 border-[#DA414A]/40'
                } hover:bg-[#2a2a2a] text-white`}
                title={isMicOn ? (language?.['mute_microphone'] || 'Mute microphone') : (language?.['unmute_microphone'] || 'Unmute microphone')}
              >
                <img
                  src={isMicOn ? Microphone : MICOFF}
                  alt="microphone"
                  height={20}
                  width={20}
                  style={{ height: '20px', width: '20px' }}
                />
              </button>
              
              {/* Microphone Dropdown */}
              <div className="relative">
                <label className="block text-white text-xs font-medium mb-1.5">
                  {language?.['select_microphone'] || 'Microphone'}
                </label>
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowMicDropdown(!showMicDropdown);
                      setShowCameraDropdown(false);
                    }}
                    className="w-full bg-[#202121] hover:bg-[#2a2a2a] text-white px-3 py-2.5 rounded-lg flex items-center justify-between border border-[#333] transition-colors min-w-[180px]"
                  >
                    <span className="text-xs truncate pr-2">{getSelectedMicLabel()}</span>
                    <AiOutlineDown className="flex-shrink-0" size={14} />
                  </button>
                  {showMicDropdown && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#202121] border border-[#333] rounded-lg shadow-xl max-h-60 overflow-y-auto z-30" style={{ maxHeight: '240px' }}>
                      {micDevices.map((device) => (
                        <button
                          key={device.deviceId}
                          onClick={() => handleMicChange(device.deviceId)}
                          className={`w-full text-left px-3 py-2.5 text-xs text-white hover:bg-[#2a2a2a] transition-colors ${
                            selectedMic === device.deviceId ? 'bg-[#DA414A]/20' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {selectedMic === device.deviceId && (
                              <span className="text-[#DA414A]">●</span>
                            )}
                            <span className={selectedMic === device.deviceId ? 'font-medium' : ''}>
                              {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Go Live Button - Prominent and Visible */}
            <div className="flex items-center sm:items-end w-full sm:w-auto justify-center sm:justify-start">
              <button
                onClick={onGoLive}
                className="group relative bg-gradient-to-r from-[#DA414A] to-[#E85D75] hover:from-[#C73841] hover:to-[#D64A63] text-white font-bold px-8 py-3 rounded-xl text-base shadow-2xl hover:shadow-[0_0_40px_rgba(218,65,74,0.6)] transition-all duration-300 transform hover:scale-110 active:scale-105 overflow-hidden w-full sm:w-auto border-2 border-[#DA414A]/30 hover:border-[#DA414A]/50"
                style={{
                  boxShadow: '0 0 20px rgba(218, 65, 74, 0.4), 0 4px 14px rgba(218, 65, 74, 0.3)'
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2 font-extrabold">
                  <span className="text-base sm:text-lg">{language?.['go_live'] || 'Go Live'}</span>
                  <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                {/* Pulsing glow effect */}
                <div className="absolute inset-0 bg-[#DA414A] opacity-20 rounded-xl animate-pulse"></div>
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showCameraDropdown || showMicDropdown) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowCameraDropdown(false);
            setShowMicDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default CameraPreview;

